import { manhattan } from "@jpjj/solver-core/prize";
import { state, enterRoom } from "./state";
import { LEVELS } from "./levels";
import { starsFor } from "./scoring";
import { $ } from "../dom";
import type { Stage } from "../three/scene";
import { openReveal, prepareSolves } from "../reveal/reveal";

let stage: Stage;

/* ---------- Floaty feedback (a DOM chip over the projected cell) ---------- */
function floaty(text: string, cell: [number, number], kind: "gain" | "loss"): void {
  if (!stage) return;
  const p = stage.cellToScreen(cell, 0.7);
  const el = document.createElement("div");
  el.className = "floaty " + kind;
  el.textContent = text;
  el.style.left = `${p.x}px`;
  el.style.top = `${p.y}px`;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 1100);
}

/* ---------- Messages ---------- */
function say(text: string, cls = ""): void {
  const m = $("msg");
  m.className = "msg" + (cls ? " " + cls : "");
  m.innerHTML = text;
}

/* ---------- HUD ---------- */
export function renderHUD(): void {
  const L = state.level();
  $("levelVal").textContent = `${state.idx + 1} / ${LEVELS.length}`;
  $("bankedVal").textContent = String(state.bankedPrize());
  $("loadVal").textContent = `${state.load()} / ${L.capacity}`;
  $("stepsVal").textContent = String(Math.max(0, state.stepsLeft()));
  $("roomName").innerHTML = `Room ${state.idx + 1} — <b>${L.name}</b>`;

  const frac = Math.min(1, state.steps / L.budget);
  const fill = $("dangerFill");
  fill.style.width = `${frac * 100}%`;
  fill.classList.toggle("hot", frac >= 0.7);
  fill.classList.toggle("max", frac >= 0.9);

  const best = Math.max(state.records[state.idx].prize, state.bankedPrize());
  const stars = starsFor(L, best);
  const starEls = $("stars").children;
  for (let i = 0; i < 3; i++) starEls[i].classList.toggle("on", i < stars);

  ($("fleeBtn") as HTMLButtonElement).disabled = state.phase !== "play";
  updateNav();
}

/** Push the live scene to match the game state (hero, loot, trail, danger). */
function syncScene(): void {
  const L = state.level();
  stage.setCarry(state.load(), L.capacity);
  stage.setTrail(L, state.trips, [0, ...state.carried]);
  stage.setDanger(state.steps / L.budget);
}

/* ---------- Tap handling ---------- */
function tapLoot(idx: number): void {
  if (state.phase !== "play") return;
  if (state.carried.includes(idx) || state.banked.includes(idx)) return;
  const L = state.level();
  const cell = L.points[idx];
  const item = L.loot[idx - 1];
  const cost = manhattan(state.hero, cell);

  if (state.load() + item.weight > L.capacity) {
    say(`Arms full (<b>${state.load()}/${L.capacity}</b>). Haul back to the cart to unload.`, "warn");
    return;
  }
  if (state.steps + cost > L.budget) {
    say("Too far — the beast would reach the room before you got back.", "warn");
    return;
  }

  state.steps += cost;
  void stage.heroTo(cell);
  state.hero = [...cell] as [number, number];
  state.carried.push(idx);
  stage.collectLoot(idx);
  floaty(`−${cost}`, cell, "loss");
  syncScene();
  renderHUD();

  if (state.steps >= L.budget) { endRoom("caught"); return; }
  say(`Carrying <b>${state.carriedPrize()}</b> gold. Tap the cart to bank it before the beast arrives.`);
}

function tapCart(): void {
  if (state.phase !== "play") return;
  const L = state.level();
  const cart = L.points[0];
  const cost = manhattan(state.hero, cart);

  if (cost === 0) {
    if (state.carried.length === 0) say("Tap the glittering loot to haul it back here.");
    return;
  }
  if (state.steps + cost > L.budget) {
    say("Not enough steps to make it back — you over-reached. Flee, or lose what you carry.", "warn");
    return;
  }

  state.steps += cost;
  void stage.heroTo(cart);
  state.hero = [...cart] as [number, number];
  const trip = [0, ...state.carried, 0];
  const gained = state.carriedPrize();
  state.trips.push(trip);
  state.banked.push(...state.carried);
  state.carried = [];
  stage.bankFlash(cart);
  if (gained > 0) floaty(`+${gained}`, cart, "gain");
  syncScene();
  renderHUD();

  if (state.steps >= L.budget) { endRoom("safe"); return; }
  say(gained > 0
    ? `Banked <b>${gained}</b> gold — <b>${state.bankedPrize()}</b> safe in the cart. Go again?`
    : "Back at the cart. Tap loot to fill the next run.");
}

/* ---------- End of room ---------- */
export function endRoom(reason: "safe" | "caught" | "flee"): void {
  const L = state.level();
  const lostPrize = state.carriedPrize();
  state.carried = [];
  state.phase = "over";

  const banked = state.bankedPrize();
  const rec = state.records[state.idx];
  if (!rec.played || banked > rec.prize) {
    state.records[state.idx] = { prize: banked, trips: state.trips.map((t) => [...t]), steps: state.steps, played: true };
  } else {
    rec.played = true;
  }

  stage.setDanger(1);
  if (reason === "caught") stage.onCaught();
  else stage.onArrivedSafe();
  stage.setCarry(0, L.capacity);
  renderHUD();

  const stars = starsFor(L, banked);
  const star = "★".repeat(stars) + "☆".repeat(3 - stars);
  let head: string;
  if (reason === "caught") head = "The minotaur lunges from the doorway!";
  else if (reason === "flee") head = "You shove the cart down the side passage.";
  else head = "The minotaur reaches the room — but you're clear of it.";
  const lost = lostPrize > 0 ? ` You dropped <b>${lostPrize}</b> gold in the scramble.` : "";
  const verdict = banked >= L.opt
    ? " A perfect haul — nothing more could be banked."
    : banked >= L.opt * 0.75
      ? " A solid run, but the optimizer banks more."
      : "";
  say(`${head} You banked <b>${banked}</b> gold. ${star}${lost}${verdict}`, banked >= L.opt ? "optimal" : "");
}

export function fleeRoom(): void {
  if (state.phase !== "play") return;
  endRoom("flee");
}

export function resetRoom(): void {
  enterRoom(state.idx);
  state.phase = "play";
  stage.loadRoom(state.level());
  syncScene();
  renderHUD();
  say("Tap loot to haul it back to the cart. Bank it before the beast reaches you.");
}

/* ---------- Navigation ---------- */
export function buildDots(): void {
  const d = $("dots");
  d.innerHTML = "";
  LEVELS.forEach((_, i) => {
    const b = document.createElement("div");
    b.className = "dot";
    b.textContent = String(i + 1);
    b.addEventListener("click", () => enter(i));
    d.appendChild(b);
  });
}

export function updateNav(): void {
  const dots = $("dots").children;
  for (let i = 0; i < dots.length; i++) {
    dots[i].classList.toggle("current", i === state.idx);
    dots[i].classList.toggle("done", state.records[i].played);
    dots[i].textContent = state.records[i].played ? "✓" : String(i + 1);
  }
  ($("prevBtn") as HTMLButtonElement).disabled = state.idx === 0;
  const next = $("nextBtn") as HTMLButtonElement;
  next.textContent = state.idx === LEVELS.length - 1 ? "Face the optimizer ✦" : "Next room ▸";
  next.classList.toggle("glow", state.phase === "over");
}

export function enter(i: number): void {
  if (i < 0 || i >= LEVELS.length) return;
  enterRoom(i);
  state.phase = "play";
  stage.loadRoom(state.level());
  syncScene();
  renderHUD();
  say("Tap loot to haul it back to the cart. Bank it before the beast reaches you.");
}

export function goPrev(): void {
  if (state.idx > 0) enter(state.idx - 1);
}

function finalizeIfPlaying(): void {
  if (state.phase !== "play") return;
  const banked = state.bankedPrize();
  const rec = state.records[state.idx];
  if (!rec.played || banked > rec.prize) {
    state.records[state.idx] = { prize: banked, trips: state.trips.map((t) => [...t]), steps: state.steps, played: true };
  } else {
    rec.played = true;
  }
}

export function goNext(): void {
  finalizeIfPlaying();
  if (state.idx < LEVELS.length - 1) { enter(state.idx + 1); return; }
  const missing = state.records.findIndex((r) => !r.played);
  if (missing === -1) {
    state.phase = "reveal";
    openReveal(stage);
  } else {
    enter(missing);
    say(`Room ${missing + 1} hasn't been raided yet — clear every room to face the optimizer.`, "warn");
  }
}

/* ---------- Lifecycle ---------- */
export function startGame(): void {
  enter(0);
  prepareSolves().catch(() => { /* handled at reveal time */ });
}

export function restartGame(): void {
  state.records = LEVELS.map(() => ({ prize: 0, trips: [], steps: 0, played: false }));
  $("solverOverlay").classList.add("hidden");
  $("endOverlay").classList.add("hidden");
  $("learnOverlay").classList.add("hidden");
  document.body.classList.remove("reveal-on");
  stage.setRevealMode(false);
  enter(0);
}

/* ---------- Pointer wiring (tap = pick, drag = orbit) ---------- */
export function initInteraction(s: Stage): void {
  stage = s;
  const canvas = stage.renderer.domElement;
  let down: { x: number; y: number; t: number } | null = null;

  canvas.addEventListener("pointerdown", (e) => {
    down = { x: e.clientX, y: e.clientY, t: performance.now() };
  });
  canvas.addEventListener("pointermove", (e) => {
    if (state.phase !== "play") { canvas.style.cursor = ""; return; }
    const overLoot = stage.pickLoot(e.clientX, e.clientY) !== null;
    canvas.style.cursor = overLoot || stage.pickCart(e.clientX, e.clientY) ? "pointer" : "";
  });
  canvas.addEventListener("pointerup", (e) => {
    if (!down) return;
    const dx = e.clientX - down.x, dy = e.clientY - down.y;
    const isTap = dx * dx + dy * dy < 80 && performance.now() - down.t < 500;
    down = null;
    if (!isTap || state.phase !== "play") return;
    const idx = stage.pickLoot(e.clientX, e.clientY);
    if (idx !== null) { tapLoot(idx); return; }
    if (stage.pickCart(e.clientX, e.clientY)) tapCart();
  });
}

/* ---------- Test hooks (used by scripts/verify.mjs) ---------- */
declare global {
  interface Window { __cr?: Record<string, unknown> }
}
export function exposeTestHooks(): void {
  if (typeof window === "undefined") return;
  window.__cr = {
    state,
    levels: LEVELS,
    tapLoot,
    tapCart,
    flee: fleeRoom,
    enter,
    goNext,
    reset: resetRoom,
    cellToScreen: (cell: [number, number], y = 0.4) => stage.cellToScreen(cell, y),
    pickLoot: (x: number, y: number) => stage.pickLoot(x, y),
    pickCart: (x: number, y: number) => stage.pickCart(x, y),
  };
}
